import {
    Button,
    Upload,
    Spin,
    Popover
} from "antd";
import StringSimilarity from "string-similarity";
import {v4} from "uuid";
import HecRasHelper from "../Helpers/HecRasHelper";
import FileHelper from "../Helpers/FileReader";
import {useState} from "react";
import {CloudDownloadOutlined, FolderOutlined, InboxOutlined} from "@ant-design/icons";
import ProjectMD5 from "../Helpers/ProjectMD5";
import {useUser} from "../Context/UserContext";
import AppColor from "./V2/AppColor";
import CloudProjects from "./V2/CloudsProject";
import PopoverScrollContent from "./V2/PopoverScrollContent";
import KMZHelper from "../Helpers/KMZHelper";
import JSZip from 'jszip'
import {maxScreensAway} from "plotly.js/src/traces/scatter/constants";
import ApiService from "../Service/ApiService";

const { Dragger } = Upload;

export const PlanTypes = [
    {label: 'Effective', match: 'Effective',},
    {label: 'Duplicate Effective', match: 'Duplicate Effective DE',},
    {label: 'Corrected Effective', match: 'Corrected Effective CE',},
    {label: 'Existing', match: 'Existing',},
    {label: 'Proposed', match: 'Proposed',},
    {label: 'Revised', match: 'Revised REV',},
    {label: 'Multiple Run', match: 'Multiple Run',},
    {label: 'Floodway Run', match: 'Floodway Run',},
    {label: 'Defined Future', match: 'Defined Future DF',},
    {label: 'Grid Mapping', match: 'Grid Mapping',},
    {label: 'Calibration', match: 'Calibration',},
]

export const PlanProfiles = [
    {label: 'FW',           visible_label: 'FW',           value: 'FW', match: 'FW'},
    {label: '2yr',    visible_label: '2yr (50%)',    value: 'YR2', match: '2YR50%'},
    {label: '10yr',   visible_label: '10yr (10%)',   value: 'YR10', match: '10YR10%'},
    {label: '25yr',    visible_label: '25yr (4%)',    value: 'YR25', match: '25YR4%'},
    {label: '50yr',    visible_label: '50yr (2%)',    value: 'YR50', match: '50YR2%'},
    {label: '100yr',   visible_label: '100yr (1%)',   value: 'YR100', match: '100YR1%'},
    {label: '500yr', visible_label: '500yr (0.2%)', value: 'YR500', match: '500YR0.2%'},
    {label: 'Future 10%',   visible_label: 'Future 10%',   value: 'FUT10', match: 'FUTURE10%'},
    {label: 'Future 20%',   visible_label: 'Future 20%',   value: 'FUT20', match: 'FUTURE20%'},
    {label: 'Future 35%',   visible_label: 'Future 35%',   value: 'FUT35', match: 'FUTURE35%'},
    {label: 'Future Ult',   visible_label: 'Future Ult',   value: 'FUTULT', match: 'FUTUREULT'},
]

export const zip = async (data) => {
    const zip = new JSZip()
    data.forEach(x => {
        zip.file(x.fileName, x.blob)
    })

    const content = await zip.generateAsync({type: "blob", compression: "DEFLATE",
        compressionOptions: {
            level: 9
        }})
    return content
}

export const unzipToJson = async (base64) => {
    const binaryData = atob(base64);
    const zip = new JSZip();
    const zipData = await zip.loadAsync(binaryData, {base64: false})
    const files = Object.keys(zipData.files)
    const json = zipData.files[files[0]]
    const text = await json.async('text')
    return JSON.parse(text)
}

export default function ProjectUploader({onUploadProject}) {
    let dropFiles = []
    let dropFolder = null

    const [spin, setSpin] = useState(false)
    const [status, setStatus] = useState('Loading')
    const [files, setFiles] = useState([])
    const [openFileDialog, setOpenFileDialog] = useState(true)
    const {user} = useUser()

    function containsOnlyNumbers(str) {
        return /^\d+$/.test(str);
    }

    function containsNumbers(str) {
        return /\d/.test(str);
    }

    const autoMapPlanType = (text, mapProfiles) => {
        text = text.toUpperCase()

        if(text.includes('ENCROACH'))
            return 'Floodway Run'

        if(text.includes('FLOODPLAIN') || text.toUpperCase().includes('MULTI'))
            return 'Multiple Run'

        if(text.includes('FUTURE'))
            return 'Defined Future'

        text = text.replaceAll('_', ' ')
        text = text.replaceAll('-', ' ')

        if(text.split(' ').find(x => x.trim() === 'DE'))
            text = text.replaceAll(' DE', ' DUPLICATE EFFECTIVE')

        if(text.split(' ').find(x => x.trim() === 'CE'))
            text = text.replaceAll(' CE', ' CORRECTED EFFECTIVE')

        if(text.split(' ').find(x => x.trim() === 'REV'))
            text = text.replaceAll(' REV', ' REVISED')

        let hasOnlyFW100 = false
        if(mapProfiles.length === 2 && mapProfiles.filter(x => x.target === 'FW' || x.target === 'YR100')) {
            hasOnlyFW100 = true
        }

        const matches = StringSimilarity.findBestMatch(text, PlanTypes.map(x => x.match.toUpperCase()));
        if(matches.bestMatch?.rating < 0.4) {
            return hasOnlyFW100 ? 'Floodway Run' : 'Multiple Run'
        }

        const index = matches.bestMatchIndex;
        return PlanTypes[index].label
    }

    const autoProfileMap = (text) => {
        let originalText = `${text}`
        text = text.toUpperCase()
        text = text.replaceAll('-', '')
        text = text.replaceAll('_', '')
        text = text.replaceAll('PCT', '%')
        text = text.replaceAll('(', '')
        text = text.replaceAll(')', '')
        text = text.replaceAll('@', '')
        text = text.replaceAll(' ', '')

        // if(text.includes('1%'))
        //     text = text.replace('1%', '100YR')

        if(text.toLowerCase() === 'floodway')
            return ['FW', 1];

        if(text.includes('FW')) {
            return ['FW', 1];
        }

        if(text.includes('1%10%')) {
            return ['FUT10', 1]
        }

        if(text.includes('YEAR')) {
            text = text.replaceAll('YEAR', 'YR')
        }

        if(text.startsWith('02')) {
            text = text.replace('02', '0.2')
        }

        try {
            if(containsOnlyNumbers(text))
                text = `${text}YR`

            if(text === '500YR')
                return ['YR500', 1]

            if(text === '50YR')
                return ['YR50', 1]

            if(text.includes('FUT') && text.includes('100')) {
                return ['FUTULT', 1]
            }

            if(text.startsWith('FC')) {
                text = text.replace('FC', 'FUTURE')
            }
        }
        catch (exp) {

        }

        if(text === '100YR10%') {
            return ['FUT10', 1]
        }

        if(text === '100YR20%') {
            return ['FUT20', 1]
        }

        if(text === '100YR35%') {
            return ['FUT35', 1]
        }

        const matches = StringSimilarity.findBestMatch(text, PlanProfiles.map(x => x.match.toUpperCase()));
        const index = matches.bestMatchIndex;

        if(matches?.bestMatch?.rating < 0.5)
        {
            if(matches.ratings.filter(x => x.rating !== 0) === 0)
                return [originalText, 1]
        }

        return [PlanProfiles[index].value.toUpperCase(), matches?.bestMatch?.rating]
    }

    const mapProjectFiles = async (allFiles) => {
        setStatus('The project files are being processed.')

        let files = [...allFiles]
        let folderName = dropFolder

        let groupedFiles = []
        let shapeFiles = []

        // shape file group
        const shpFiles = files.filter(x => x?.webkitRelativePath?.toLowerCase()?.endsWith('.shp'))

        for (const shpFile of shpFiles) {
            const relativePath = shpFile?.webkitRelativePath?.replaceAll('.shp', '')
            const shapeFiles = files.filter(y => y.webkitRelativePath.includes(relativePath))

            shapeFiles.forEach(x => {
                files = files.filter(y => y.webkitRelativePath !== x.webkitRelativePath)
            })

            const data = []
            for (const shapeFile of shapeFiles) {
                if(data.find(x => x.fileName === shapeFile.name))
                    continue

                data.push({
                    fileName: shapeFile.name,
                    blob: shapeFile
                })
            }
            const blob = await zip(data)

            groupedFiles.push({
                id: v4(),
                fileName: shpFile.name,
                ext: 'shp',
                title: shpFile.name,
                data: blob,
                type: 'shape-file',
            })
        }


        for(let i=0; i<files.length; i++) {
            let file = files[i];

            const split = file.name.split('.');
            if(split.length < 2)
                continue

            const ext = split[split.length-1].toLowerCase();

            if(ext?.toLowerCase()?.includes('kml')) {
                const blob = await zip([{fileName: file.name, blob: file}])

                const f = {
                    id: v4(),
                    fileName: file.name,
                    ext,
                    title: file.name,
                    data: blob,
                    type: 'shape-file',
                }
                groupedFiles.push(f)
            }
            else if(ext?.toLowerCase()?.includes('kmz')) {
                const blob = await zip([{fileName: file.name, blob: file}])

                const f = {
                    id: v4(),
                    fileName: file.name,
                    ext,
                    title: file.name,
                    data: blob,
                    type: 'shape-file',
                }
                groupedFiles.push(f)
            }
            else if(ext?.toLowerCase()?.includes('geojson') || ext.includes('json')) {
                const buffer = await FileHelper.readTextFile(file)
                const f = {
                    id: v4(),
                    fileName: file.name,
                    ext,
                    title: file.name,
                    data: buffer,
                    type: 'shape-file',
                }
                groupedFiles.push(f)
            }
            else if(ext?.toLowerCase()?.includes('prj'))
            {
                if(shapeFiles.find(x => x.fileName === file.name))
                    continue

                const text = await FileHelper.readTextFile(file)
                if(
                    text.includes('Geom File') &&
                    text.includes('Plan File') &&
                    text.includes('Flow File') &&
                    text.includes('Proj Title')
                ) {
                    const f = {
                        id: v4(),
                        fileName: file.name,
                        folderName: getRelativePathFolder(file),
                        title: HecRasHelper.getProjectTitle(text),
                        descriptions: HecRasHelper.getProjectDescription(text),
                        text: text,
                        type: 'project'
                    }

                    groupedFiles.push(f)
                }
                else if(text?.includes('PROJCS')) {
                    groupedFiles.push({
                        id: v4(),
                        fileName: file.name,
                        title: 'Projection File',
                        descriptions: '',
                        text,
                        type: 'projection'
                    })
                }
            }
            else if(ext?.toLowerCase()?.startsWith('g'))
            {
                const text = await FileHelper.readTextFile(file)
                const f = {
                    id: v4(),
                    fileName: file.name,
                    ext,
                    title: HecRasHelper.getGeometryTitle(text),
                    text: text,
                    type: 'geometry'
                }

                groupedFiles.push(f)
            }
            else if(ext?.toLowerCase()?.startsWith('p'))
            {
                const text = await FileHelper.readTextFile(file)
                const version = HecRasHelper.getPlanVersion(text)
                const f = {
                    id: v4(),
                    fileName: file.name,
                    ext,
                    title: HecRasHelper.getPlanTitle(text),
                    shortIdentifier: HecRasHelper.getPlanShortIdentifier(text),
                    version: HecRasHelper.getPlanVersion(text),
                    text: text,
                    geometryFileExt: HecRasHelper.getGeometryFile(text),
                    flowFileExt: HecRasHelper.getFlowFile(text),
                    type: 'plan',
                    isMapped: false
                }

                groupedFiles.push(f)
            }
            else if(ext?.toLowerCase()?.startsWith('f'))
            {
                const text = await FileHelper.readTextFile(file)
                const profiles = HecRasHelper.getFlowProfiles(text);
                const f = {
                    id: v4(),
                    fileName: file.name,
                    ext,
                    title: HecRasHelper.getFlowTitle(text),
                    profiles: profiles,
                    text: text,
                    type: 'flow'
                }

                groupedFiles.push(f)
            }
            else if(ext?.toLowerCase()?.includes('txt')) {
                const text = await FileHelper.readTextFile(file)
                const f = {
                    id: v4(),
                    fileName: file.name,
                    ext,
                    title: file.name,
                    text: text,
                    type: 'output',
                    data: [],
                }

                if (f.fileName === 'output.txt') {
                    const out = HecRasHelper.parseAllOutputProfiles(f.text);
                    if(out.length > 1)
                        f.data = HecRasHelper.parseAllOutputProfiles(f.text).filter(x => x.Plan)
                    else
                        f.data = out;

                    groupedFiles.push(f)
                }
            }
        }

        const fileNames = Array.from(new Set(groupedFiles.map(x => x.fileName)))
        const uniqFiles = fileNames.map(x => {
            return groupedFiles.find(y => y.fileName === x)
        })
        groupedFiles = [...uniqFiles]

        let projectFiles = []
        const _planFiles = groupedFiles.filter(x => x.type === 'plan');
        const output = groupedFiles.find(y => y.type === 'output')

        _planFiles.forEach(plan => {
            const fileName = plan.fileName.split('.')[0]

            let flowFile = groupedFiles.find(y => plan.flowFileExt?.toLowerCase()?.includes(y.ext) && y.fileName?.toLowerCase()?.includes(fileName))
            if(!flowFile)
                flowFile = groupedFiles.find(y => plan.flowFileExt?.toLowerCase()?.includes(y.ext))

            let geoFile = groupedFiles.find(y => plan.geometryFileExt?.toLowerCase()?.includes(y.ext) && y.fileName?.toLowerCase()?.includes(fileName))
            if(!geoFile)
                geoFile = groupedFiles.find(y => plan.geometryFileExt?.toLowerCase()?.includes(y.ext))

            if(!geoFile)
            {
                return
            }

            if(!flowFile)
            {
                return
            }

            let profileData = {}
            if(output) {
                if(output.data.length === 1) {
                    profileData = output.data[0]
                }
                else {
                    profileData = output.data.find(x => x.Plan.trim() === plan.title.trim() || x.Plan === plan.shortIdentifier.trim())
                    if(!profileData)
                        profileData = output.data.find(x => x.Plan.trim() === flowFile.title.trim() || x.Plan.trim() === geoFile.title.trim())
                }
            }

            const mapProfiles = flowFile ? flowFile.profiles.map(x => {
                const [key, rating] = autoProfileMap(x);
                return {select: x, target: key, targetName: PlanProfiles.find(x => x.value === key)?.label || key, rating}
            }) : []

            let network_count = 0;
            mapProfiles.forEach((x, idx) => {
                if(idx+1 >= mapProfiles.length)
                    return

                const next_profile = mapProfiles[idx+1]
                if(parseFloat(next_profile?.select) - parseFloat(x?.select) === 0.5) {
                    network_count++
                }
            })

            if(network_count > 3) {
                mapProfiles.map(x => {
                    x.target = x.select
                    x.targetName = x.select
                })
            }
            else {
                const targetProfiles = Array.from(new Set(mapProfiles.map(x => x.target)))
                targetProfiles.forEach(p => {
                    const profiles = mapProfiles
                        .filter(x => x.target === p)
                        .sort((a,b) => parseFloat(a.rating) - parseFloat(b.rating))
                        .reverse()

                    if(profiles.length > 1) {
                        profiles.forEach((x, idx) => {
                            // best match skip
                            if(idx === 0)
                                return

                            x.target = x.select
                            x.targetName = x.select
                        })
                    }
                })
            }

            projectFiles.push({
                id: v4(),
                plan: plan,
                geometry: geoFile,
                flow: flowFile,
                mapProfiles: mapProfiles,
                profileData: profileData,
                mappedProfiles: HecRasHelper.mapProfiles(profileData, mapProfiles),
                type: autoMapPlanType(plan.title, mapProfiles),
                isMapped: true
            })
        })

        const information = groupedFiles.find(x => x.type === 'project');
        const _project = {
            information: {
                ...information,
                folderName: information.folderName,
                hasOutputFile: output ? 'true' : 'false',
            },
            id: information?.id,
            shapeFiles: groupedFiles.filter(x => x.type === 'shape-file'),
            projection: groupedFiles.find(x => x.type === 'projection'),
            projectFiles: [...projectFiles],
            files: groupedFiles,
        }


        for (let i=0; i<_project.shapeFiles.length; i++) {
            const shapeFile = _project.shapeFiles[i]
            setStatus(`${shapeFile?.title} are being processed. This process may take some time. (${i+1}/${_project.shapeFiles.length})`)
            try {
                if(shapeFile.ext?.toLowerCase() === 'geojson')
                    continue

                const base64 = await FileHelper.readAsBase64(shapeFile.data)
                const res = await ApiService.RasCheck.ShpToGeoJson({
                    zip: base64,
                    type: shapeFile.ext
                })

                if(res.success){
                    const geojson = await unzipToJson(JSON.parse(res.data)?.result)
                    _project.shapeFiles[i] =  {
                        id: v4(),
                        fileName: `${shapeFile.fileName}.geojson`,
                        ext: 'geojson',
                        title: `${shapeFile.fileName}.geojson`,
                        data: geojson,
                        type: 'shape-file',
                    }
                }
            }
            catch (exp) {
                console.log(exp)
            }
        }

        _project.projectFiles = _project?.projectFiles?.map(prj => {
            return {
                ...prj,
                projectHash: ProjectMD5({
                    email: user.data.email,
                    projectTitle: _project.information.title,
                    flowTitle: prj.flow.title,
                    flowFile: prj.flow.fileName,
                    geoTitle: prj.geometry.title,
                    geoFile: prj.geometry.fileName,
                    planTitle: prj.plan.title,
                    planFile: prj.plan.fileName
                })
            }
        })

        _project.projectFiles = _project.projectFiles.filter(
            (obj, index) =>
                _project.projectFiles.findIndex((item) => item.projectHash === obj.projectHash) === index
        );

        _project.projectFiles.map(plan => {
            plan.rivers = getRivers(plan).map(x => {
                x.csList = getRiverCSList(plan?.geometry?.text, x.river, x.reach)
                return x
            })
            plan.hasGeoReferenced = HecRasHelper.hasGeoReferenced(plan?.geometry?.text)
            return plan
        })


        _project.projectFiles.map(plan => {
            if(plan?.rivers?.length > 1) {
                const allRivers = Array.from(new Set(plan?.rivers?.map(x => x.river)))
                if(allRivers.length > 1 && _project?.information?.riverReachColumns?.length > 0) {
                    plan.needRiverColumn = true
                }
                else {
                    plan.needRiverColumn = false
                }
            }
            else {
                plan.needRiverColumn = false
            }

            return plan
        })


        if(_project?.information?.hasOutputFile === 'false') {
            _project.projectFiles = _project.projectFiles.map(x => {
                x.mapProfiles = []
                x.mappedProfiles = []
                return x
            })
        }

        return _project
    }

    const getRivers = (plan) => {
        const rivers = []
        plan.geometry?.text?.split('\n')?.forEach(line => {
            if(line.startsWith('River Reach=')) {
                const splitted = line.split('=')
                if(splitted.length > 0) {
                    const props = splitted[splitted.length-1].split(',').map(x => x.trim())
                    rivers.push({
                        river: props[0],
                        reach: props[1],
                        key: `${props[0]}___${props[1]}`,
                    })
                }
            }
        })

        return rivers
    }



    const getRiverCSList = (geoFile, river, reach) => {
        const lines = geoFile?.split('\n')
        const csList = []

        let isRiverStart = false
        lines.forEach((line, idx) => {
            if(line?.startsWith('River Reach=') && line?.includes(river) && line?.includes(reach)) {
                isRiverStart = true
            }

            if(isRiverStart && line?.startsWith('River Reach=') &&  (!line?.includes(river) || !line?.includes(reach)))
                isRiverStart = false

            if(isRiverStart) {
                if(line?.includes('Type RM Length L Ch R =')) {
                    const props = line.split('=')[1]?.split(',').map(x => x.trim())
                    csList.push({
                        name: props[1],
                        value: parseFloat(props[1]),
                        type: props[0]
                    })
                }
            }
        })

        return csList
    }

    const getRelativePathFolder = (file) => {
        const split = file?.webkitRelativePath?.replaceAll('\\', '/')?.split('/')
        split?.pop()
        return split?.join('/')
    }

    const groupProjects = async (dropFiles) => {
        const prjFiles = []
        for (const dropFile of dropFiles) {
            if(dropFile?.name?.toLowerCase()?.endsWith('.prj')) {
                const text = await FileHelper.readTextFile(dropFile)
                if(text?.includes('Proj Title=')
                    && text?.includes('Geom File=')
                    && text?.includes('Flow File=')
                    && text?.includes('Plan File=')
                )
                {
                    prjFiles.push(dropFile)
                }
            }
        }

        //webkitRelativePath
        const projects = []
        for (const prjFile of prjFiles) {
            const otherPaths = prjFiles?.filter(x => x?.webkitRelativePath !== prjFile?.webkitRelativePath).map(x => getRelativePathFolder(x))
            const files = prjFiles?.length > 1 ? dropFiles?.filter(df => !otherPaths.includes(getRelativePathFolder(df))) : dropFiles
            const mappedFiles = HecRasHelper.checkProjectFiles(await mapProjectFiles(files))
            projects.push(mappedFiles)
        }

        return projects
    }


    let timeout = null
    let allFiles = []
    const onChangeFiles = async (info) => {
        const path = info?.fileList[0]?.originFileObj?.webkitRelativePath;
        if(path && !dropFolder) {
            dropFolder = path.split('/')[0];
        }

        const split = info.file.name.split('.')
        const ext = split[split.length-1]
        if(!ext?.startsWith('O')) {
            dropFiles.push(info)
        }

        if(timeout) {
            clearTimeout(timeout)
            timeout = null
        }

        timeout = setTimeout(async () => {
            try {
                setSpin(true)
                allFiles = []
                for (const dropFile of dropFiles) {
                    for (const file of dropFile.fileList) {
                        if(file?.originFileObj) {
                            if(!allFiles.find(x => x?.webkitRelativePath === file?.originFileObj.webkitRelativePath)) {
                                allFiles.push(file?.originFileObj)
                            }
                        }
                        else if(file?.webkitRelativePath) {
                            if(!allFiles.find(x => x?.webkitRelativePath === file?.webkitRelativePath)) {
                                allFiles.push(file)
                            }
                        }
                    }
                }

                // dropFiles.forEach(files => {
                //     allFiles = [...allFiles, ...files.fileList.map(x => {
                //         return (x && x.originFileObj) || x
                //     })]
                // })

                const projects = await groupProjects(allFiles)
                onUploadProject(projects)

                setSpin(false)
            } catch (exp) {
                console.log(exp)
            }
        }, 1500)
    }

    return (
        <>
            <Dragger
                openFileDialogOnClick={openFileDialog}
                onChange={onChangeFiles}
                multiple={true}
                showUploadList={false}
                beforeUpload={() => false}
                fileList={files}
                directory={true}
            >
                {
                    !spin && (
                        <>
                            <div
                                 style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexDirection: 'column'}}>
                                <div
                                    style={{cursor: "pointer", height: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column'}}
                                >
                                    <InboxOutlined style={{fontSize: '25pt', color: AppColor.mainColor}}/>
                                    Drag & Drop
                                </div>
                                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                    <div>
                                        <Button
                                            style={{height: '100%', display: 'flex', alignItems: 'center', width: 250,  justifyContent: 'center'}}
                                        >
                                            <FolderOutlined style={{fontSize: '15pt', color: AppColor.mainColor}}/>
                                            Browse HEC-RAS Model Folder
                                        </Button>
                                    </div>
                                    <Popover
                                        trigger={'click'}
                                        onOpenChange={(open) => {
                                            if(!open)
                                                setOpenFileDialog(true)
                                            else
                                                setOpenFileDialog(false)
                                        }}
                                        content={(
                                            <PopoverScrollContent>
                                                <CloudProjects
                                                    onUpload={(prj) => {
                                                        prj = HecRasHelper.checkProjectFiles(prj)
                                                        onUploadProject([prj])
                                                    }}
                                                />
                                            </PopoverScrollContent>
                                        )}
                                    >
                                        <Button
                                            onClick={(e) => {
                                                setOpenFileDialog(false)
                                                e.stopPropagation();
                                                e.preventDefault();
                                            }}
                                            style={{height: '100%', display: 'flex', alignItems: 'center', width: 250, justifyContent: 'center'}}
                                        >
                                            <CloudDownloadOutlined
                                                style={{fontSize: '15pt', color: AppColor.mainColor}}/>
                                            Saved Projects
                                        </Button>
                                    </Popover>
                                </div>
                            </div>
                        </>
                    )
                }
                {
                    spin && (
                        <>
                            <div style={{margin: 8}}>
                                <Spin tip={status} size="large">
                                </Spin>
                            </div>
                        </>
                    )
                }
            </Dragger>
        </>
    )
}